import type { PostHogContextRef } from '../context'
import { buildInChatContextProperties } from '../context'

/**
 * Constrained string union of backend enriched-document keys. Open enum
 * — the list grows as backend capabilities expand. Values keep their
 * native backend casing per the spec's external-systems exception.
 */
export type EnrichedDocumentType =
  | 'tarot'
  | 'settings'
  | 'contentLink'
  | 'oracleBegin'
  | 'oracleChoose'
  | 'oracleFlip'

/**
 * Per-tarot-document shape. Future document types may define their own
 * `enriched_document_info` shape; field is intentionally free-form here.
 */
export interface EnrichedTarotDocumentInfo {
  card: string
  position: number
  reversed: boolean
}

/**
 * Divination flow events: enriched-document arrival from the chat GraphQL
 * subscription. Replaces the legacy per-flavor events
 * (ai_tarot_begin / ai_tarot_initial_card / ai_tarot_clarifying_card /
 * ai_oracle_draw) with one unified event whose payload distinguishes flavors.
 */
export type DivinationEvents = {
  ai_enriched_document_received: (
    documentType: EnrichedDocumentType,
    documentInfo: EnrichedTarotDocumentInfo[] | unknown,
    uiElement: string,
  ) => void
}

export function createDivinationEventCallbacks (
  contextRef: PostHogContextRef,
): DivinationEvents {
  return {
    ai_enriched_document_received: (
      documentType: EnrichedDocumentType,
      documentInfo: EnrichedTarotDocumentInfo[] | unknown,
      uiElement: string,
    ) => {
      const ctx = contextRef.current
      if (!ctx.posthogClient) return
      ctx.posthogClient.capture('ai_enriched_document_received', {
        ...buildInChatContextProperties(ctx),
        enriched_document_type: documentType,
        enriched_document_info: documentInfo,
        ui_element: uiElement,
      })
    },
  }
}
